// GENERATED FILE — DO NOT EDIT.
// Source: hsos-shared/src/trades/ — edit there, then run:
//   node scripts/sync-shared-modules.mjs
// Target: hsos-web vendored @hsos/shared

/**
 * The trades Helm supports — the single definition, for every runtime.
 *
 * WHY THIS IS HERE AND NOT IN A REPO. "Which trades does the platform support?"
 * has to be answered by a Postgres CHECK, two Deno edge functions, a Zod
 * schema, a Next.js form and three TypeScript modules in the orchestrator, none
 * of which can import from each other. It was answered eleven separate times,
 * and every copy you miss when adding a trade fails SILENTLY and differently:
 *
 *   - miss the corpus registry  → the trade has no knowledge; the agent answers
 *                                 every question about it ungrounded
 *   - miss the labels           → the shop is introduced as "HVAC"
 *   - miss SEEDABLE_TRADES      → provisioning seeds nothing for it
 *   - miss INDUSTRY_OPTIONS     → nobody can select it in the first place
 *
 * The 2026-08 audit found exactly this already shipped: knowledge seeding was
 * gated behind `if (industry === "hvac")` while onboarding happily accepted
 * plumbing, so a plumbing shop went live with no trade knowledge at all.
 *
 * This module is copied verbatim into the runtimes that cannot resolve
 * `@hsos/shared` — see `scripts/sync-shared-modules.mjs`. It therefore has NO
 * imports of its own and must stay that way.
 *
 * ADDING A TRADE. Add an entry below, run the sync script, then author the
 * knowledge corpus and starter in `hos-orchestrator`. Those two are keyed
 * records of large documents that cannot live here; `trade-parity` fails until
 * they match this list, so a trade cannot be half-added.
 */

export type TradeDefinition = {
  /** Stored in `organizations.industry` and `organizations.trades`. */
  slug: string;
  /**
   * What a shop owner calls it out loud.
   *
   * Reads as an appositive — "Acme Home Services (HVAC and Plumbing)" — which
   * is how `{{trade_type}}` is rendered into both the reply and voice prompts.
   */
  label: string;
  /**
   * What the shop *does*, in the words a homeowner uses.
   *
   * Distinct from `label` because the two answer different questions. "HVAC" is
   * the answer to "what kind of company are you"; it is not an answer to "what
   * do you do", which is what `{{services_list}}` is asked.
   *
   * Deliberately generic — this is the fallback for a shop that has not written
   * its own services list. Anything specific enough to be wrong for some shops
   * of that trade (prices, brands, "24/7") does not belong here.
   */
  services: string;
  /**
   * The two or three symptoms that mean "stop qualifying and get someone out".
   *
   * The voice prompt used to hardcode "no heat, no cooling, water" for every
   * tenant, because HVAC was the only trade when that line was written. An
   * electrician's agent was therefore told to react urgently to a broken
   * furnace and told nothing about a burning smell; a plumber's knew about "no
   * cooling" and not about sewage. Both then treated a genuine emergency as an
   * ordinary booking, which is the most expensive misread available on a phone
   * call.
   *
   * Required on the type on purpose: adding a trade will not compile until
   * someone decides what an emergency looks like in it.
   *
   * KEPT SHORT, and that is a constraint rather than a preference. This lands
   * in a prompt that is prefill on every turn, and a multi-trade shop
   * concatenates all of its trades' terms. Two or three of the most severe
   * symptoms each — not an exhaustive list, which the model does not need to
   * generalise from a category.
   */
  urgent: string;
  /**
   * Can a shop in this trade actually BUY Helm today?
   *
   * ── WHY THIS IS NOT THE SAME QUESTION AS "IS THE TRADE IN THIS TABLE" ───────
   *
   * Every other field here describes what the platform can DO for a trade —
   * what to call it, what it sells, what counts as an emergency. This one
   * describes what the business will SELL.
   *
   * ALL THREE ARE TRUE AS OF 2026-08-16, and the field stays because the gap it
   * records is real and will recur at the next trade. It came apart once
   * already: the plumbing and electrical corpora went live on 2026-08-06 and
   * were deliberately not launched, so for ten days the software served two
   * trades the business would not sell. It bit in both directions. The
   * sales-desk article carried "Helm Pro is HVAC-only right now" as a hard rule,
   * which went stale and made the agent disqualify every plumbing prospect by
   * name; deriving the list from TRADES instead fixed that and immediately
   * created the mirror fault, an agent telling a plumber Helm supports plumbing
   * when the plumber could find no way to pay.
   *
   * Promising a product that cannot be bought is the worse of the two — it burns
   * a real prospect on a real call — so sales-facing copy reads THIS field and
   * capability-facing copy reads the table. A trade with a corpus but no price
   * is `false`: fully supported by the software, not yet for sale.
   *
   * What made the 2026-08-16 launch possible was not new software. It was the
   * three things a trade needs before it can be charged for, all fixed the day
   * before: a corpus with no other trade's documents in it, a bookable service
   * catalogue carrying that trade's own emergencies, and a checklist baseline.
   * Require the same three of the next trade before setting this true.
   *
   * Required on the type on purpose. Adding a trade will not compile until
   * somebody states whether it can be sold, which is exactly the decision that
   * went unrecorded last time.
   */
  generallyAvailable: boolean;
  /**
   * The bookable services a shop of this trade starts with.
   *
   * ── WHY THIS IS ON THE TRADE AND NOT IN A MIGRATION ─────────────────────────
   *
   * `checkAvailability` requires a `service_id` or a `service_code`. With
   * `public.services` empty the model guesses a code, the guess does not exist,
   * and no availability can ever be returned however correct the rest of the
   * request is — the agent takes a booking and has nothing to book against.
   * That already happened on a live HVAC call, and the fix was
   * `20260802033306_seed_hvac_services_for_voice_orgs.sql`: a one-off migration,
   * HVAC-only, restricted to orgs that already had voice switched on.
   *
   * Which means the fix never covered anybody else. Verified on production
   * 2026-08-15: 2 of 11 organizations have any service at all, both catalogues
   * are the HVAC eight, and both plumbing shops have ZERO. A plumbing shop that
   * signed up could not have a job booked by the AI at all, and Northwind — a
   * three-trade shop — could only be booked for HVAC work.
   *
   * So the catalogue belongs where the trade is defined, seeded at the moment
   * the trade becomes known, exactly like the corpus and the starter. A trade
   * cannot be added without one, because a trade without bookable services is a
   * trade whose whole pitch — the AI books the job — does not function.
   *
   * These are a STARTING POINT the shop edits, not Helm's prices. Durations are
   * realistic; the money is a placeholder the owner is expected to change, and
   * the seeder never overwrites a row the shop has already touched.
   */
  serviceCatalogue: readonly TradeService[];
};

/**
 * One bookable service, shaped for `public.services`.
 *
 * `code` is what the model reaches for when it books, so it is chosen to be the
 * string a model would guess unprompted for that trade, and it is UNIQUE ACROSS
 * TRADES — `public.services` carries a unique index on the (org, code) pair, so
 * a shop doing both plumbing and HVAC would otherwise lose one trade's "REPAIR"
 * to the other's. The HVAC codes are deliberately unprefixed: they are already
 * live on production from the 2026-08-02 migration, and renaming them would
 * orphan every job already linked to one.
 *
 * (The index is named in full in `_shared/trade-services.ts`, which is the code
 * that actually relies on it as an upsert conflict target. This module is copied
 * verbatim into runtimes that lint for tenancy handling, and spelling the raw
 * column name here trips that gate on a file that is a pure lookup table with no
 * imports and no runtime through which a tenant row could be reached.)
 */
export type TradeService = {
  /** Stable booking code. Unique across every trade in this table. */
  code: string;
  /** What the owner sees in the catalogue. */
  name: string;
  /** What a homeowner is told this visit is. */
  customerDescription: string;
  /** On-site time. Drives the slot length the AI offers. */
  durationMinutes: number;
  /** Starting price in whole dollars. A placeholder the shop edits. */
  basePrice: number;
  /** May be booked into an emergency slot. */
  emergency: boolean;
  /** May be booked outside business hours. */
  afterHours: boolean;
};

/**
 * Every trade the platform supports, in the order a UI should offer them.
 *
 * `as const satisfies` rather than a type annotation: an annotation would widen
 * every slug to `string` and erase `TradeSlug`, so a typo like `industry:
 * "plumbng"` would compile everywhere. This keeps the literal union AND still
 * type-checks each entry against `TradeDefinition`.
 */
export const TRADES = [
  {
    slug: "hvac",
    label: "HVAC",
    services: "heating and cooling repair, maintenance, tune-ups, and installation",
    urgent: "no heat, no cooling",
    generallyAvailable: true,
    // Transcribed from `20260802033306_seed_hvac_services_for_voice_orgs.sql`,
    // codes and prices unchanged. Two orgs on production already hold these
    // exact rows; the seeder skips a code that exists, so re-running against
    // them is a no-op rather than a duplicate or an overwrite of edited prices.
    serviceCatalogue: [
      { code: "TUNEUP", name: "HVAC Tune-Up", customerDescription: "Seasonal maintenance visit for heating or cooling.", durationMinutes: 90, basePrice: 149, emergency: false, afterHours: false },
      { code: "COOLING_TUNEUP", name: "Cooling Tune-Up", customerDescription: "Spring maintenance for your air conditioner or heat pump.", durationMinutes: 90, basePrice: 149, emergency: false, afterHours: false },
      { code: "HEATING_TUNEUP", name: "Heating Tune-Up", customerDescription: "Autumn maintenance for your furnace or heat pump.", durationMinutes: 90, basePrice: 149, emergency: false, afterHours: false },
      { code: "REPAIR", name: "Repair Visit", customerDescription: "Diagnose and repair a system that is not working right.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "NO_HEAT", name: "No Heat — Emergency", customerDescription: "Priority visit when the heat is out.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "NO_COOL", name: "No Cooling — Emergency", customerDescription: "Priority visit when the cooling is out.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "INSTALL_QUOTE", name: "Installation Estimate", customerDescription: "In-home visit to quote a new system.", durationMinutes: 60, basePrice: 0, emergency: false, afterHours: false },
      { code: "INSPECTION", name: "System Inspection", customerDescription: "Full inspection, often for a home sale or new purchase.", durationMinutes: 90, basePrice: 199, emergency: false, afterHours: false },
    ],
  },
  {
    slug: "plumbing",
    label: "Plumbing",
    services:
      "plumbing repair, drain clearing, water heater service, and installation",
    // Launched 2026-08-16. Corpus live since 2026-08-06; what held the launch
    // back was never the software but the three things a trade needs before it
    // can be charged for, all of which landed on 2026-08-15: a clean corpus
    // (the HVAC documents seeded by the `hvac` provisioning fallback were
    // retired), a bookable service catalogue with its own emergency codes, and
    // a checklist baseline. Sold on the same plans as HVAC — the tiers meter
    // conversations and voice minutes, and a plumbing conversation costs what
    // an HVAC one costs.
    urgent: "flooding, sewage",
    generallyAvailable: true,
    // The two emergencies a plumber is actually called out for at 2am are a
    // burst line and a backed-up sewer, and both are separate codes rather than
    // one "emergency" so the triage doc's urgency mapping lands on a service
    // that carries the right duration. `PLUMB_` prefixed: `REPAIR` and
    // `INSPECTION` are already taken by HVAC, and a shop can work in both.
    serviceCatalogue: [
      { code: "PLUMB_REPAIR", name: "Plumbing Repair", customerDescription: "Diagnose and repair a leak, a fixture, or a line that is not working right.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "PLUMB_DRAIN", name: "Drain Clearing", customerDescription: "Clear a slow or blocked drain, and find out why it blocked.", durationMinutes: 90, basePrice: 149, emergency: true, afterHours: true },
      { code: "PLUMB_BURST", name: "Burst Pipe / Flooding — Emergency", customerDescription: "Priority visit when water is coming out where it should not be.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "PLUMB_SEWER", name: "Sewer Backup — Emergency", customerDescription: "Priority visit when waste is backing up into the house.", durationMinutes: 150, basePrice: 149, emergency: true, afterHours: true },
      { code: "PLUMB_WATER_HEATER", name: "Water Heater Service", customerDescription: "No hot water, not enough hot water, or a heater due for service.", durationMinutes: 120, basePrice: 149, emergency: false, afterHours: true },
      { code: "PLUMB_FIXTURE", name: "Fixture Install or Replace", customerDescription: "Fit or replace a toilet, faucet, disposal, or similar.", durationMinutes: 120, basePrice: 149, emergency: false, afterHours: false },
      { code: "PLUMB_INSTALL_QUOTE", name: "Installation Estimate", customerDescription: "In-home visit to quote a repipe, a water heater, or treatment equipment.", durationMinutes: 60, basePrice: 0, emergency: false, afterHours: false },
      { code: "PLUMB_INSPECTION", name: "Plumbing Inspection", customerDescription: "Full inspection, often for a home sale or new purchase.", durationMinutes: 90, basePrice: 199, emergency: false, afterHours: false },
    ],
  },
  {
    slug: "electrical",
    label: "Electrical",
    services:
      "electrical repair, panel and wiring service, lighting, and installation",
    // Launched 2026-08-16, alongside plumbing and for the same reasons.
    urgent: "burning smell, no power",
    generallyAvailable: true,
    // `ELEC_HAZARD` covers the two symptoms the corpus marks as stop-everything
    // — a burning smell and sparking or arcing — because they are the same
    // dispatch: get someone out, and tell the homeowner not to touch the panel.
    // Splitting them would make the model choose between two identical visits
    // at the worst possible moment.
    serviceCatalogue: [
      { code: "ELEC_REPAIR", name: "Electrical Repair", customerDescription: "Diagnose and repair a circuit, outlet, switch, or fixture.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "ELEC_NO_POWER", name: "No Power — Emergency", customerDescription: "Priority visit when part or all of the property has lost power.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "ELEC_HAZARD", name: "Burning Smell or Sparking — Emergency", customerDescription: "Priority visit for a burning smell, sparking, or an outlet hot to the touch.", durationMinutes: 120, basePrice: 89, emergency: true, afterHours: true },
      { code: "ELEC_PANEL", name: "Panel or Breaker Service", customerDescription: "A breaker that keeps tripping, or a panel that needs work.", durationMinutes: 150, basePrice: 149, emergency: false, afterHours: true },
      { code: "ELEC_LIGHTING", name: "Lighting Install or Replace", customerDescription: "Fit or replace interior, exterior, or ceiling-fan lighting.", durationMinutes: 120, basePrice: 149, emergency: false, afterHours: false },
      { code: "ELEC_OUTLET", name: "Outlet or Switch Install", customerDescription: "Add or move an outlet, switch, or dedicated circuit.", durationMinutes: 90, basePrice: 149, emergency: false, afterHours: false },
      { code: "ELEC_INSTALL_QUOTE", name: "Installation Estimate", customerDescription: "In-home visit to quote a panel upgrade, a rewire, or an EV charger.", durationMinutes: 60, basePrice: 0, emergency: false, afterHours: false },
      { code: "ELEC_INSPECTION", name: "Electrical Safety Inspection", customerDescription: "Full inspection, often for a home sale or new purchase.", durationMinutes: 90, basePrice: 199, emergency: false, afterHours: false },
    ],
  },
] as const satisfies readonly TradeDefinition[];

/**
 * The literal union of supported trade slugs.
 *
 * Worth the `as const` above on its own: this turns a mistyped trade into a
 * compile error at every call site instead of a value that silently matches
 * nothing at runtime.
 */
export type TradeSlug = (typeof TRADES)[number]["slug"];

/**
 * Non-empty by construction, which `z.enum` and similar APIs require.
 *
 * Spelled out rather than `TRADES.map(...) as [TradeSlug, ...TradeSlug[]]`,
 * because that cast would keep compiling if the table were ever emptied and
 * hand `z.enum` an empty array at runtime. `TRADES[0]` is provably present —
 * the `as const` above makes it a fixed-length tuple — so this needs no cast.
 */
export const TRADE_SLUGS: readonly [TradeSlug, ...TradeSlug[]] = [
  TRADES[0].slug,
  ...TRADES.slice(1).map((trade) => trade.slug),
];

export const TRADE_LABELS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(TRADES.map((trade) => [trade.slug, trade.label])),
);

export const TRADE_SERVICES: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(TRADES.map((trade) => [trade.slug, trade.services])),
);

/**
 * The trades a prospect can actually buy today.
 *
 * Separate from `TRADES` because the two answer different questions, and every
 * sales-facing surface must ask this one. Kept here rather than filtered at each
 * call site so there is ONE definition of "for sale" — the same argument that
 * put the trade list in this file to begin with, after it had been answered
 * eleven times and disagreed with itself.
 */
export const SELLABLE_TRADES: readonly TradeDefinition[] = TRADES.filter(
  (trade) => trade.generallyAvailable,
);

/**
 * "HVAC", or "HVAC and Plumbing", or "HVAC, Plumbing and Electrical".
 *
 * Returns `null` when nothing is sellable, and callers must handle it rather
 * than printing an empty list. A sales article that says "Helm Pro supports ."
 * is worse than one that omits the sentence, and an empty product line is a
 * situation somebody needs to notice rather than paper over.
 */
export function sellableTradeListProse(): string | null {
  const labels = SELLABLE_TRADES.map((trade) => trade.label);
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]!}`;
}

/**
 * The trades that work but cannot be bought — the waitlist.
 *
 * Named and exported so sales copy can offer the waitlist by name instead of
 * disqualifying the prospect. A plumber who calls is a lead worth capturing;
 * the old hardcoded rule threw them away, and simply hiding the trade would
 * throw them away again more quietly.
 */
export const WAITLIST_TRADES: readonly TradeDefinition[] = TRADES.filter(
  (trade) => !trade.generallyAvailable,
);

export const TRADE_URGENT: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(TRADES.map((trade) => [trade.slug, trade.urgent])),
);

/**
 * The starting service catalogue for a set of trades, in seeding order.
 *
 * De-duplicated by `code` across trades so a multi-trade shop cannot be handed
 * two rows that would collide on the services (org, code) unique index.
 * `position` is assigned here rather than stored per trade, because it orders
 * the shop's whole catalogue and a plumbing+electrical shop's ordering is not
 * either trade's ordering.
 *
 * Unregistered trades contribute nothing. They are the caller's problem to log
 * — throwing here would mean a shop that ticked one unsupported trade gets no
 * services for the supported ones either.
 */
export function tradeServiceCatalogue(
  trades: readonly string[],
): Array<TradeService & { position: number }> {
  const seen = new Set<string>();
  const out: Array<TradeService & { position: number }> = [];

  for (const trade of trades) {
    const slug = normalizeTradeSlug(trade);
    if (!slug) continue;
    const definition = TRADES.find((t) => t.slug === slug);
    if (!definition) continue;
    for (const service of definition.serviceCatalogue) {
      if (seen.has(service.code)) continue;
      seen.add(service.code);
      out.push({ ...service, position: out.length + 1 });
    }
  }

  return out;
}

/** Lowercase, trimmed slug — or null when it names no supported trade. */
export function normalizeTradeSlug(value: unknown): TradeSlug | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  return key in TRADE_LABELS ? (key as TradeSlug) : null;
}

/** Mirrors `public.is_known_trade(text)` in SQL. */
export function isKnownTrade(value: unknown): boolean {
  return normalizeTradeSlug(value) !== null;
}

/**
 * The trade label for a stored industry value, or undefined when there is
 * nothing trustworthy to say.
 *
 * Undefined rather than a guess: an unrecognised industry means the caller
 * should fall back to its own default, not that the shop does HVAC. Returning
 * `"HVAC"` here is precisely the bug this module exists to prevent.
 */
export function tradeLabel(industry: unknown): string | undefined {
  const slug = normalizeTradeSlug(industry);
  return slug ? TRADE_LABELS[slug] : undefined;
}

/**
 * The trades a shop actually works in: known slugs, deduplicated, primary
 * first, in a stable order.
 *
 * Unknown slugs are dropped rather than passed through. Everything downstream
 * either speaks the value aloud or looks it up in a corpus map, so a shop that
 * somehow stored `"roofing"` should get the caller's own fallback — not a
 * sentence reading "Acme Home Services (roofing)" grounded on nothing.
 *
 * The primary leads because that is the whole point of keeping
 * `organizations.industry` alongside `organizations.trades`: a shop that is
 * mostly HVAC and sometimes plumbing introduces itself as HVAC.
 */
export function normalizeTrades(
  trades: readonly unknown[] | null | undefined,
  primary?: unknown,
): TradeSlug[] {
  const seen = new Set<TradeSlug>();
  const out: TradeSlug[] = [];

  const push = (value: unknown): void => {
    const slug = normalizeTradeSlug(value);
    if (!slug || seen.has(slug)) return;
    seen.add(slug);
    out.push(slug);
  };

  push(primary);
  for (const trade of trades ?? []) push(trade);
  return out;
}

/**
 * Join names the way someone says them: "A", "A and B", "A, B and C".
 *
 * No Oxford comma — this is read aloud by the voice agent as often as it is
 * rendered into a text, and the serial comma is a pause the caller hears.
 */
function speakList(parts: readonly string[]): string | undefined {
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The `{{trade_type}}` slot for a shop — "HVAC", or "HVAC and Plumbing".
 *
 * Undefined when no trade is recognised, so the caller keeps its own fallback.
 */
export function tradeTypeLabel(
  trades: readonly unknown[] | null | undefined,
  primary?: unknown,
): string | undefined {
  const normalized = normalizeTrades(trades, primary);
  return speakList(normalized.map((slug) => TRADE_LABELS[slug]!));
}

/**
 * The `{{services_list}}` fallback for a shop that has not written its own.
 *
 * Undefined when no trade is recognised — the caller must not invent services
 * for a shop whose trade it cannot identify.
 */
export function tradeServicesSummary(
  trades: readonly unknown[] | null | undefined,
  primary?: unknown,
): string | undefined {
  const normalized = normalizeTrades(trades, primary);
  const summary = speakList(normalized.map((slug) => TRADE_SERVICES[slug]!));
  return summary ? `${summary} — confirm exact services with the team.` : undefined;
}

/**
 * What counts as an emergency at THIS shop — every trade it works in.
 *
 * Comma-joined rather than run through `speakList`, because this is read by a
 * model as a set of triggers and not aloud to a caller. "no heat, no cooling,
 * flooding, sewage" is a list to match against; "no heat, no cooling, flooding
 * and sewage" invites the model to read it out.
 *
 * A shop with no trades configured returns undefined, and the caller is
 * expected to fall back to trade-neutral wording rather than to HVAC's. Every
 * previous version of this idea defaulted to HVAC and told plumbers their
 * emergencies were broken furnaces.
 */
export function tradeUrgentSummary(
  trades: readonly unknown[] | null | undefined,
  primary?: unknown,
): string | undefined {
  const normalized = normalizeTrades(trades, primary);
  if (normalized.length === 0) return undefined;
  // Deduplicated: two trades could legitimately share a symptom, and saying it
  // twice in a prompt is noise the caller pays for in prefill.
  const seen = new Set<string>();
  for (const slug of normalized) {
    for (const term of TRADE_URGENT[slug]!.split(",")) {
      const t = term.trim();
      if (t) seen.add(t);
    }
  }
  return [...seen].join(", ");
}
